import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ChatList from './components/ChatList';
import ChatWindow from './components/ChatWindow';
import PeoplePickerDialog from './components/PeoplePickerDialog';
import { useChat } from './ChatContext';
import './chat.css';

// Вкладка «Чат»: слева список, справа переписка.
const ChatPage = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'ru';

  const {
    chats, chatsLoaded, activeChatId, setActiveChatId, loadHistory,
    togglePinned, toggleMuted, socketError, activityByUser, openDirectChat, createGroup,
    connectionState, reconnect,
  } = useChat();

  const [historyError, setHistoryError] = useState(null);
  const [newChatOpen, setNewChatOpen] = useState(false);
  const [newGroupOpen, setNewGroupOpen] = useState(false);

  const openChat = useCallback(async (chat) => {
    setActiveChatId(chat.id);
    setHistoryError(null);
    try {
      await loadHistory(chat.id, { reset: true });
    } catch {
      setHistoryError(t('chat.errors.history'));
    }
  }, [loadHistory, setActiveChatId, t]);

  // Первый чат открываем автоматически — так же, как в списочных разделах админки.
  useEffect(() => {
    if (!activeChatId && chats.length > 0) {
      openChat(chats[0]);
    }
  }, [activeChatId, chats, openChat]);

  // Выбор человека в диалоге: создаём (или переоткрываем) личный чат и сразу открываем его.
  const handlePickPeer = useCallback(async (user) => {
    const chat = await openDirectChat(user);
    if (chat) await openChat(chat);
  }, [openChat, openDirectChat]);

  // Создание группы: возвращённый ChatSummaryDto сразу кладём в список и открываем.
  const handleCreateGroup = useCallback(async ({ title, memberIds }) => {
    const chat = await createGroup(title, memberIds);
    if (chat) await openChat(chat);
  }, [createGroup, openChat]);

  const activeChat = chats.find((chat) => chat.id === activeChatId) || null;

  return (
    <div className="chat-page">
      <header className="chat-page-head">
        <h1 className="chat-page-title">{t('chat.title')}</h1>
        <p className="chat-page-subtitle">{t('chat.subtitle')}</p>
      </header>

      {(socketError || historyError) && (
        <div className="form-status error" role="alert">
          {historyError || socketError}
        </div>
      )}

      {connectionState !== 'connected' && (
        <div className={`chat-connection chat-connection--${connectionState}`}>
          <span className="chat-connection-dot" />
          <span>
            {connectionState === 'failed'
              ? t('chat.connection.failed')
              : connectionState === 'reconnecting'
                ? t('chat.connection.reconnecting')
                : t('chat.connection.connecting')}
          </span>
          {connectionState === 'failed' && (
            <button type="button" className="chat-connection-retry" onClick={reconnect}>
              {t('chat.connection.retry')}
            </button>
          )}
        </div>
      )}

      <div className="chat-shell">
        <ChatList
          chats={chats}
          activeChatId={activeChatId}
          onSelect={openChat}
          onTogglePinned={togglePinned}
          onToggleMuted={toggleMuted}
          locale={locale}
          loading={!chatsLoaded}
          activityByUser={activityByUser}
          onNewChat={() => setNewChatOpen(true)}
          onNewGroup={() => setNewGroupOpen(true)}
        />
        <ChatWindow chat={activeChat} locale={locale} />
      </div>

      <PeoplePickerDialog
        open={newChatOpen}
        mode="direct"
        onClose={() => setNewChatOpen(false)}
        onSubmit={handlePickPeer}
      />

      <PeoplePickerDialog
        open={newGroupOpen}
        mode="group"
        onClose={() => setNewGroupOpen(false)}
        onSubmit={handleCreateGroup}
      />
    </div>
  );
};

export default ChatPage;
